import { TrendPairData } from '../types';

/**
 * Trend Data Service Module
 * 
 * This module handles loading and processing trend data from external sources.
 * It includes functions for fetching, parsing, and transforming data into
 * the format needed by the application.
 */

/**
 * LoadTrendData: Fetches and processes search term popularity data
 * 
 * This function:
 * 1. Fetches trend data from a CSV file in the public directory
 * 2. Parses the CSV into structured data
 * 3. Creates pairs of terms to compare
 * 4. Formats the data for use in the game
 * 
 * @returns Promise resolving to an array of trend pairs ready for the game
 */
export const loadTrendData = async (): Promise<TrendPairData[]> => {
  try {
    // Step 1: Fetch CSV data from the public directory
    // The PUBLIC_URL environment variable ensures correct path in all environments
    const csvUrl = `${process.env.PUBLIC_URL}/data/search_terms_popularity.csv`;
    const response = await fetch(csvUrl);
    
    // Handle fetch errors
    if (!response.ok) {
      throw new Error(`Failed to fetch trend data: ${response.status} ${response.statusText}`);
    }
    
    // Get CSV content as text
    const csvText = await response.text();
    
    // Step 2: Parse CSV data into structured format
    // Skip header row and parse each data row
    const rows = csvText.split('\n').slice(1);
    
    // Transform each row into a structured object
    const trendData = rows
      .filter(row => row.trim()) // Skip empty rows
      .map(row => {
        const [Term, Average_Interest] = row.split(',');
        return {
          Term,
          Average_Interest: parseFloat(Average_Interest)
        };
      });

    // Step 3: Sort data for deterministic pairing
    // This ensures the same pairs are created every time, regardless of CSV order
    const sortedData = [...trendData].sort((a, b) => 
      a.Term.localeCompare(b.Term)
    );
    
    // Step 4: Form pairs for the game
    // Take terms two at a time to create comparable pairs
    const pairs: TrendPairData[] = [];
    for (let i = 0; i < sortedData.length - 1; i += 2) {
      const leftTrend = sortedData[i];
      const rightTrend = sortedData[i + 1];
      
      // Create a pair with all required information for the game
      pairs.push({
        left: leftTrend.Term,
        right: rightTrend.Term,
        // Set which term is more popular (for determining correct answers)
        correctIsLeft: leftTrend.Average_Interest > rightTrend.Average_Interest,
        // Include popularity scores for showing results
        averageInterestLeft: leftTrend.Average_Interest,
        averageInterestRight: rightTrend.Average_Interest
      });
    }

    return pairs;
  } catch (error) {
    // Log error and return empty array to allow graceful failure
    console.error('Error loading trend data:', error);
    return []; // Return empty array on error
  }
};